import { groupBy } from "lodash";
import React from "react";
import { colors } from "src/styles";
import { ID, TeamMember } from "src/types";
import styled from "styled-components";
import { RoleFilter } from "../TeamMembersFilter";

export interface ClientsTableProps {
  headers: string[];
  data: TeamMember[];
  onRowClick: (id: ID) => void;
  roleFilterData?: RoleFilter;
}

const TeamMembersTable = ({ headers, data, onRowClick, roleFilterData }: ClientsTableProps) => {
  if (!data.length) {
    return <EmptyRow>No team members found.</EmptyRow>;
  }

  return (
    <TableWrap>
      <TableHeader>
        {headers.map((header) => (
          <span key={header}>{header}</span>
        ))}
      </TableHeader>
      {data.map(({ employeeId, employeeDisplayName, clients, locations, roles, onSite }: TeamMember) => {
        const currentRole =
          roleFilterData &&
          Object.keys(roles).map((role) => {
            return role === "employee" ? { label: roles.employee } : roleFilterData[role];
          });

        const groupedByClientLocations = groupBy(locations, ({ clientId }) => clientId) || [];
        const isOnSite = !!(onSite && Object.keys(onSite).length);

        return (
          <TableRow key={employeeId} onClick={() => onRowClick(employeeId)}>
            <IdPill>{employeeId}</IdPill>
            <NameCell>{employeeDisplayName}</NameCell>
            <StackCell>
              {clients.length ? (
                clients.map((client) => {
                  const currentClientLocations = groupedByClientLocations[client.id] || [];
                  return (
                    <ClientLine key={client.id}>
                      {client.name}
                      {!!currentClientLocations.length && (
                        <ClientMeta>{`${currentClientLocations.length} site${currentClientLocations.length === 1 ? "" : "s"}`}</ClientMeta>
                      )}
                    </ClientLine>
                  );
                })
              ) : (
                <ItalicDash>-</ItalicDash>
              )}
            </StackCell>
            <StackCell>
              {locations.length ? (
                locations.map((location) => <MutedLine key={location.id}>{location.name}</MutedLine>)
              ) : (
                <ItalicDash>-</ItalicDash>
              )}
            </StackCell>
            <StackCell>
              {currentRole?.length ? (
                currentRole.map((role) => <Role key={role?.label}>{role?.label}</Role>)
              ) : (
                <MutedLine>Employee</MutedLine>
              )}
            </StackCell>
            <StatusCell>
              <StatusPill $isActive={isOnSite}>{isOnSite ? "On Site" : "Not On Site"}</StatusPill>
            </StatusCell>
          </TableRow>
        );
      })}
    </TableWrap>
  );
};

const EmptyRow = styled.div`
  padding: 20px 16px;
  color: ${colors.grey.dark6};
  font-size: 13px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const TableGridBase = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(220px, 1.1fr) minmax(220px, 1fr) minmax(260px, 1.2fr) 180px 140px;
  gap: 8px;
  align-items: center;
  min-width: 1120px;
`;

const TableHeader = styled(TableGridBase)`
  padding: 12px 16px;
  color: ${colors.grey.dark6};
  border-bottom: 1px solid ${colors.grey.light3};
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
`;

const TableRow = styled(TableGridBase)`
  padding: 14px 16px;
  border-top: 1px solid ${colors.grey.light3};
  cursor: pointer;

  &:hover {
    background: ${colors.grey.light6};
  }
`;

const IdPill = styled.span`
  border: 1px solid ${colors.grey.light4};
  background: ${colors.grey.light6};
  font-size: 11px;
  font-weight: 700;
  color: ${colors.black.light1};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 8px;
  width: fit-content;
`;

const NameCell = styled.span`
  font-size: 13px;
  color: ${colors.black.dark1};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StackCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const ClientLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  color: ${colors.black.dark1};
  font-size: 13px;
`;

const ClientMeta = styled.span`
  color: ${colors.grey.dark6};
  font-size: 11px;
  white-space: nowrap;
`;

const MutedLine = styled.div`
  color: ${colors.grey.dark6};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Role = styled.div`
  color: ${colors.grey.dark6};
  font-size: 12px;
`;

const StatusCell = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const StatusPill = styled.span<{ $isActive: boolean }>`
  border: 1px solid ${({ $isActive }) => ($isActive ? colors.green.default : colors.grey.light4)};
  background: ${({ $isActive }) => ($isActive ? colors.green.light : colors.grey.light6)};
  color: ${({ $isActive }) => ($isActive ? colors.green.default : colors.grey.dark6)};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 5px 8px;
`;

const ItalicDash = styled.span`
  color: ${colors.grey.dark7};
  font-style: italic;
`;

export default TeamMembersTable;
